import {Dispatch, SetStateAction} from 'react'
import {FileRejection, useDropzone} from 'react-dropzone'
import {isAxiosError} from 'axios'
import i18next from 'i18next'
import {useTranslation} from '@/translations/i18n'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Attachment02Icon, CheckCircleIcon, AlertCircleIcon, XCloseIcon} from '@/components/ui/icon/Icon'
import {useUploadBugReportAttachment} from '../../hooks/useUploadBugReportAttachment'
import {AttachmentUploadState} from '../../types'
import {
    StyledDropzoneArea,
    StyledDropzoneHint,
    StyledDropzoneRoot,
    StyledFileError,
    StyledFileList,
    StyledFileMain,
    StyledFileName,
    StyledFileRow,
    StyledFileStatus,
    StyledRemoveButton
} from './style'

export const MAX_ATTACHMENT_BYTES = 10_000_000

interface AttachmentsDropzoneProps {
    attachments: AttachmentUploadState[]
    onChange: Dispatch<SetStateAction<AttachmentUploadState[]>>
}

const newId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`

const extractUploadErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
        const message = error.response?.data?.message
        if (typeof message === 'string' && message.length > 0) return message
    }
    return i18next.t('errors:default')
}

export const AttachmentsDropzone = ({attachments, onChange}: AttachmentsDropzoneProps) => {
    const {t} = useTranslation()
    const upload = useUploadBugReportAttachment()

    const patchRow = (id: string, patch: Partial<AttachmentUploadState>) =>
        onChange(prev => prev.map(row => (row.id === id ? {...row, ...patch} : row)))

    const removeRow = (id: string) => onChange(prev => prev.filter(row => row.id !== id))

    const startUpload = async (id: string, file: File) => {
        patchRow(id, {status: 'uploading', error: null})
        try {
            const formData = new FormData()
            formData.append('file', file)
            const result = await upload.mutateAsync(formData)
            patchRow(id, {status: 'done', assetUrl: result.assetUrl, error: null})
        } catch (error) {
            patchRow(id, {status: 'error', assetUrl: null, error: extractUploadErrorMessage(error)})
        }
    }

    const describeRejection = (rejection: FileRejection): string => {
        const codes = rejection.errors.map(error => error.code)
        if (codes.includes('file-too-large')) return t('bug_reporter:attachment_error_too_large')
        if (codes.includes('file-invalid-type')) return t('bug_reporter:attachment_error_invalid_type')
        return t('errors:default')
    }

    const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
        const rejectedRows: AttachmentUploadState[] = rejections.map(rejection => ({
            id: newId(),
            file: rejection.file,
            status: 'error',
            assetUrl: null,
            error: describeRejection(rejection)
        }))
        const acceptedRows: AttachmentUploadState[] = accepted.map(file => ({
            id: newId(),
            file,
            status: 'pending',
            assetUrl: null,
            error: null
        }))

        onChange(prev => [...prev, ...rejectedRows, ...acceptedRows])
        acceptedRows.forEach(row => startUpload(row.id, row.file))
    }

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop: handleDrop,
        accept: {'image/*': [], 'video/*': []},
        multiple: true,
        maxSize: MAX_ATTACHMENT_BYTES
    })

    return (
        <StyledDropzoneRoot>
            <StyledDropzoneArea {...getRootProps()} $active={isDragActive}>
                <input {...getInputProps()} />
                <Attachment02Icon size={20} />
                <span>{t('bug_reporter:attachment_placeholder')}</span>
                <StyledDropzoneHint>{t('bug_reporter:attachment_hint')}</StyledDropzoneHint>
            </StyledDropzoneArea>

            {attachments.length > 0 && (
                <StyledFileList>
                    {attachments.map(row => (
                        <StyledFileRow key={row.id}>
                            <StyledFileMain>
                                <StyledFileName>{row.file.name}</StyledFileName>
                                {row.status === 'error' && row.error && <StyledFileError>{row.error}</StyledFileError>}
                            </StyledFileMain>

                            {row.status === 'uploading' && (
                                <StyledFileStatus $variant="uploading">
                                    <Spinner size={16} />
                                </StyledFileStatus>
                            )}
                            {row.status === 'done' && (
                                <StyledFileStatus $variant="done">
                                    <CheckCircleIcon size={16} />
                                </StyledFileStatus>
                            )}
                            {row.status === 'error' && (
                                <StyledFileStatus $variant="error">
                                    <AlertCircleIcon size={16} />
                                </StyledFileStatus>
                            )}

                            <StyledRemoveButton
                                type="button"
                                onClick={() => removeRow(row.id)}
                                aria-label={t('commons:delete')}
                            >
                                <XCloseIcon size={16} />
                            </StyledRemoveButton>
                        </StyledFileRow>
                    ))}
                </StyledFileList>
            )}
        </StyledDropzoneRoot>
    )
}
